import { ReactElement, useEffect } from 'react'
import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'

import { Loader } from '@10xLabs/react-web/components/Loader'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { Button } from 'components/v2/Button'
import { PlacesFilters } from 'features/Places/components/PlacesFilters'
import { IPlacesFiltersForm } from 'features/Places/types'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { mapFiltersToSearchQueryString, mapSearchToFiltersForm } from 'features/Places/utils'
import { PlacesPagination } from 'features/Places/views/Index/Pagination'
import { GET_PLACES } from 'features/Places/queries-legacy'
import { useLogin } from 'features/Login/hooks'
import { canRead, canWrite } from 'features/Users/utils'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useCompanyAutocomplete } from 'features/Companies/hooks'
import { usePlace } from 'features/Places/hooks'
import { useCity } from 'features/Cities/hooks'
import { GTTPlacesResponse } from 'gql/types.generated'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { PlacesList } from './List'
import { useBusinessSiteAutocomplete } from 'features/BusinessSites/hooks'

const PLACES_LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'places.helmet',
    defaultMessage: 'Places | Nexbus Admin'
  }
})

const PlacesView = (): ReactElement => {
  let content
  const intl = useIntl()
  const login = useLogin()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const searchQueries = getQueries(location.search)
  const placeAutocomplete = usePlace(searchQueries.placeId, {
    skip: !searchQueries.placeId
  })
  const cityAutocomplete = useCity(searchQueries.cityId, {
    skip: !searchQueries.cityId
  })
  const companyAutocomplete = useCompanyAutocomplete(searchQueries.companyId, {
    skip: !searchQueries.companyId
  })
  const businessClientAutocomplete = useBusinessClientAutocomplete(searchQueries.businessClientId, {
    skip: !searchQueries.businessClientId
  })
  const businessSiteAutocomplete = useBusinessSiteAutocomplete(searchQueries.businessSiteId, {
    skip: !searchQueries.businessSiteId
  })
  const pageNb = parseInt(searchParams.get('page') || '1')
  const filters = mapSearchToFiltersForm(searchQueries, {
    place: placeAutocomplete,
    city: cityAutocomplete,
    company: companyAutocomplete,
    businessClient: businessClientAutocomplete,
    businessSite: businessSiteAutocomplete
  })

  const { loading, error, data, refetch, networkStatus } = useQuery<{ tplaces: GTTPlacesResponse }>(GET_PLACES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        size: PLACES_LIMIT,
        page: pageNb
      },
      input: filterEmptyValue({
        stateCode: searchQueries.stateCode,
        id: searchQueries.placeId,
        cityID: searchQueries.cityId,
        companyID: searchQueries.companyId,
        businessClientID: searchQueries.businessClientId,
        businessSiteID: searchQueries.businessSiteId,
        code: searchQueries.code
      })
    }
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  useEffect(() => {
    refetch()
  }, [])

  if (!canRead(FEATURES.PLACE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PLACE} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="places.error.description"
            defaultMessage="There was an error while loading your places"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="places.error.action" defaultMessage="Reload your places" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.tplaces.items) && pageNb === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="places.empty.description" defaultMessage="You don&#39;t have any places yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/places/new" kind="primary">
            <FormattedMessage id="places.empty.action" defaultMessage="Add your first place" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.tplaces.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="places.empty.filters.description"
            defaultMessage="There was no places found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/places" kind="primary">
            <FormattedMessage id="places.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <PlacesList places={data?.tplaces.items || []} />
        <PlacesPagination
          pageNb={pageNb}
          totalItems={data?.tplaces.total || 0}
          limit={PLACES_LIMIT}
          filters={filters}
          currentItemsCount={data?.tplaces.items.length || 0}
        />
      </>
    )
  }

  const handleFiltersSubmit = (filters: IPlacesFiltersForm): void => {
    navigate(`/places?${mapFiltersToSearchQueryString(filters)}`)
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="places.header.label" defaultMessage="Places" />
        </Heading.H3>
        {canWrite(FEATURES.PLACE, login.accessToken.policies) && (
          <Button as={NavLink} to="/places/new" kind="primary">
            <FormattedMessage id="places.header.new" defaultMessage="+ Create place" />
          </Button>
        )}
      </ContentHeader>
      <ContentBody>
        <PlacesFilters filters={filters} onSubmit={handleFiltersSubmit} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(PlacesView)
